/**
 * LEAVING WITH UNSAVED CHANGES ASKS FIRST — in our own dialog, never a native
 * confirm. The app runs on a plain <BrowserRouter> (no data router, so no
 * `useBlocker`), so in-app links are caught in the capture phase and replayed
 * through `navigate` once the operator says leave. Callers with their own
 * navigation (a tab bar, a back button) use `requestLeave`.
 *
 * Closing the browser tab can only be guarded by `beforeunload`, which the
 * browser draws itself — that is the one prompt we cannot style.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useInRouterContext, useNavigate } from 'react-router-dom';

export function useUnsavedChangesGuard(dirty: boolean) {
  /* Outside a router (a panel mounted on its own, as in tests) links fall back
     to a full page load. Router presence never changes for a mounted panel,
     so the conditional hook call is stable. */
  const inRouter = useInRouterContext();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const routerNavigate = inRouter ? useNavigate() : null;
  const navigate = useCallback((to: string) => {
    if (routerNavigate) routerNavigate(to); else window.location.assign(to);
  }, [routerNavigate]);
  const [pending, setPending] = useState<null | (() => void)>(null);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    const onClick = (e: MouseEvent) => {
      if (!dirtyRef.current || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as HTMLElement | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!a || a.target === '_blank' || a.hasAttribute('download')) return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;
      e.preventDefault();
      e.stopPropagation();
      setPending(() => () => navigate(url.pathname + url.search + url.hash));
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('click', onClick, true);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('click', onClick, true);
    };
  }, [dirty, navigate]);

  /** Run `go` now when clean; otherwise ask first. */
  const requestLeave = useCallback((go: () => void) => {
    if (!dirtyRef.current) { go(); return; }
    setPending(() => go);
  }, []);

  const dialog = pending ? (
    <UnsavedChangesDialog
      onStay={() => setPending(null)}
      onLeave={() => { const go = pending; setPending(null); go(); }}
    />
  ) : null;

  return { requestLeave, dialog };
}

export const UnsavedChangesDialog: React.FC<{ onStay: () => void; onLeave: () => void }> = ({ onStay, onLeave }) => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" onClick={onStay}>
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="pt-unsaved-title"
      className="w-full max-w-[440px] overflow-hidden rounded-2xl bg-white shadow-2xl"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="border-b border-stone-200 px-5 py-4">
        <h3 id="pt-unsaved-title" className="text-[15px] font-semibold text-stone-900">Leave without saving?</h3>
      </div>
      <p className="px-5 py-4 text-[13px] leading-relaxed text-stone-600">
        You have payment-type changes that have not been saved or sent to the till. Leaving discards them.
      </p>
      <div className="flex items-center justify-end gap-2 border-t border-stone-200 px-5 py-3.5">
        <button type="button" onClick={onStay} className="min-h-[44px] rounded-xl border border-stone-200 px-4 text-sm font-semibold text-stone-700 hover:bg-stone-50">
          Stay
        </button>
        <button type="button" onClick={onLeave} className="min-h-[44px] rounded-xl bg-ruby px-4 text-sm font-semibold text-white hover:bg-ruby-deep">
          Discard and leave
        </button>
      </div>
    </div>
  </div>
);
